import { ApiError, ForbiddenError, isAbortError, NetworkError, SessionExpiredError } from './errors';

export interface HttpClientOptions {
  baseUrl: string;
  /** Access token hiện tại (chỉ giữ trong bộ nhớ). */
  getAccessToken: () => string | null;
  /** Làm mới phiên. Trả về true nếu thành công. Được gọi single-flight. */
  refreshSession: () => Promise<boolean>;
  /** Gọi khi phiên không thể phục hồi (401 sau refresh hoặc refresh thất bại). */
  onSessionExpired: () => void;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  /** Số lần thử lại thêm cho GET khi lỗi mạng / 502-504. */
  maxGetRetries?: number;
  sleep?: (ms: number) => Promise<void>;
  /** Live Backend uses Success<T>; browser sessions stay in HttpOnly cookies. v3: `{"data": …}` không có meta. */
  responseEnvelope?: 'raw' | 'v2' | 'v3';
  cookieSession?: boolean;
  getCsrfToken?: () => string | null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** Bắt buộc cho mọi lệnh ghi có thể bị gửi trùng (đặt chuyến, hủy, SOS…). */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** false ⇒ không gắn token và không thử refresh khi 401 (login/refresh). */
  auth?: boolean;
  ifMatch?: number;
}

export interface ApiResponse<T> {
  status: number;
  data: T;
  headers: Headers;
}

export interface HttpClient {
  request<T = unknown>(path: string, options?: RequestOptions): Promise<ApiResponse<T>>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const RETRYABLE_GET_STATUS = new Set([502, 503, 504]);

export function createHttpClient(opts: HttpClientOptions): HttpClient {
  const fetchImpl = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const timeoutMs = opts.timeoutMs ?? 15_000;
  const maxGetRetries = opts.maxGetRetries ?? 2;
  const sleep = opts.sleep ?? defaultSleep;

  let refreshing: Promise<boolean> | null = null;
  const refreshOnce = (): Promise<boolean> => {
    // Nhiều request cùng dính 401 ⇒ chỉ MỘT lần refresh (tránh đua token).
    refreshing ??= opts.refreshSession().catch(() => false).finally(() => { refreshing = null; });
    return refreshing;
  };

  const buildUrl = (path: string, query?: RequestOptions['query']) => {
    // Absolute API/BFF paths are rooted at the configured backend origin. This
    // Keeps BFF and v2 paths rooted even when the launcher supplies a v3 base.
    const base = opts.baseUrl.replace(/\/$/, '');
    const rooted = path.startsWith('/api/v2/') || path.startsWith('/auth/');
    const originBase = base.replace(/\/api(?:\/v[23])?$/, '');
    const url = new URL((rooted ? originBase : base) + path, globalThis.location?.origin ?? 'http://localhost');
    if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    // baseUrl tương đối ('/api') ⇒ trả lại dạng tương đối cho proxy/mock
    return opts.baseUrl.startsWith('http') ? url.toString() : url.pathname + url.search;
  };

  async function once<T>(path: string, o: RequestOptions, allowRefresh: boolean): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (o.body !== undefined) headers['Content-Type'] = 'application/json';
    if (o.idempotencyKey) headers['Idempotency-Key'] = o.idempotencyKey;
    if (o.ifMatch !== undefined) {
      if (!Number.isSafeInteger(o.ifMatch) || o.ifMatch < 1) throw new ApiError(422, 'INVALID_ETAG', 'Phiên bản không hợp lệ');
      headers['If-Match'] = `"${o.ifMatch}"`;
    }
    if (opts.cookieSession && !['GET', 'HEAD', 'OPTIONS'].includes(o.method ?? 'GET')) {
      const csrf = opts.getCsrfToken?.() ?? readCookie('gsm_csrf');
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }
    if (o.auth !== false && !opts.cookieSession) {
      const token = opts.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
    const onCallerAbort = () => ctrl.abort(o.signal?.reason);
    if (o.signal?.aborted) onCallerAbort();
    o.signal?.addEventListener('abort', onCallerAbort, { once: true });

    let res: Response;
    try {
      res = await fetchImpl(buildUrl(path, o.query), {
        method: o.method ?? 'GET',
        headers,
        body: o.body !== undefined ? JSON.stringify(o.body) : undefined,
        signal: ctrl.signal,
        credentials: opts.cookieSession ? 'include' : 'same-origin',
      });
    } catch (e) {
      if (o.signal?.aborted) throw e; // người gọi hủy ⇒ giữ nguyên AbortError
      throw new NetworkError(undefined, { cause: e });
    } finally {
      clearTimeout(timer);
      o.signal?.removeEventListener('abort', onCallerAbort);
    }

    if (res.status === 401 && o.auth !== false) {
      if (allowRefresh && (await refreshOnce())) return once<T>(path, o, false);
      opts.onSessionExpired();
      throw new SessionExpiredError();
    }

    const payload = await readBody(res);
    if (res.status === 403) {
      const p = asErrorPayload(payload);
      throw new ForbiddenError(p.code ?? 'FORBIDDEN', p.message);
    }
    if (!res.ok) {
      const p = asErrorPayload(payload);
      const requestId = res.headers.get('X-Request-Id') ?? p.requestId;
      throw new ApiError(res.status, p.code ?? `HTTP_${res.status}`, p.message ?? `Lỗi ${res.status}`, p.details, requestId);
    }
    let data = payload;
    if (opts.responseEnvelope === 'v3' && res.status !== 204) {
      const envelope = payload as { data?: unknown } | null;
      if (!envelope || typeof envelope !== 'object' || !Object.hasOwn(envelope, 'data')) {
        throw new ApiError(502, 'INVALID_RESPONSE', 'Máy chủ trả dữ liệu không đúng contract v3', undefined, res.headers.get('X-Request-Id') ?? undefined);
      }
      data = envelope.data;
    }
    if (opts.responseEnvelope === 'v2' && res.status !== 204) {
      const envelope = payload as { data?: unknown; meta?: { schema_version?: string; request_id?: string } } | null;
      if (!envelope || typeof envelope !== 'object' || !Object.hasOwn(envelope, 'data') ||
          envelope.meta?.schema_version !== '2.0.0' || typeof envelope.meta.request_id !== 'string') {
        throw new ApiError(502, 'INVALID_RESPONSE', 'Máy chủ trả dữ liệu không đúng contract v2');
      }
      data = envelope.data;
    }
    return { status: res.status, data: data as T, headers: res.headers };
  }

  return {
    async request<T>(path: string, o: RequestOptions = {}): Promise<ApiResponse<T>> {
      const isGet = (o.method ?? 'GET') === 'GET';
      const attempts = isGet ? maxGetRetries + 1 : 1;
      let lastError: unknown;
      for (let i = 0; i < attempts; i++) {
        try {
          return await once<T>(path, o, true);
        } catch (e) {
          lastError = e;
          if (isAbortError(e)) throw e;
          const retryable = e instanceof NetworkError || (e instanceof ApiError && RETRYABLE_GET_STATUS.has(e.status));
          if (!isGet || !retryable || i === attempts - 1) throw e;
          await sleep(300 * 2 ** i);
        }
      }
      throw lastError;
    },
  };
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function asErrorPayload(p: unknown): { code?: string; message?: string; details?: unknown; requestId?: string } {
  if (p && typeof p === 'object') {
    const o = p as Record<string, unknown>;
    const inner = (o.error && typeof o.error === 'object' ? o.error : o) as Record<string, unknown>;
    return {
      code: typeof inner.code === 'string' ? inner.code : undefined,
      message: typeof inner.detail === 'string' ? inner.detail : typeof inner.message === 'string' ? inner.message : undefined,
      details: inner.errors ?? inner.details,
      // problem+json v3 có request_id trong thân lỗi
      requestId: typeof inner.request_id === 'string' ? inner.request_id : undefined,
    };
  }
  return {};
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${name}=`));
  if (!value) return null;
  try { return decodeURIComponent(value.slice(name.length + 1)); } catch { return null; }
}
